/* @preserve Side effect: custom Quill blot registration */
import Quill from "quill";
import "./quillBlots/passiveVoice";
import "./quillBlots/longSentence";

const Embed = Quill.import("blots/block/embed");

class CustomImageBlot extends Embed {
  static blotName = "customImage";
  static tagName = "div";
  static className = "ql-customImage";

  static create(value) {
    const node = super.create();
    node.classList.add(CustomImageBlot.className);
    node.setAttribute("role", "img");
    if (value.alt) {
      node.setAttribute("aria-label", value.alt);
    }
    node.setAttribute("tabindex", "0");

    const picture = document.createElement("picture");

    const sourceWebP = document.createElement("source");
    sourceWebP.setAttribute("type", "image/webp");
    sourceWebP.setAttribute(
      "srcset",
      Array.isArray(value.variants)
        ? value.variants.map(v => `${v.webp} ${v.width}w`).join(", ")
        : value.src
    );
    sourceWebP.setAttribute(
      "sizes",
      "(max-width: 600px) 400px, (max-width: 1200px) 800px, 1600px"
    );

    const sourceJpeg = document.createElement("source");
    sourceJpeg.setAttribute("type", "image/jpeg");
    sourceJpeg.setAttribute(
      "srcset",
      Array.isArray(value.variants)
        ? value.variants.map(v => `${v.jpeg} ${v.width}w`).join(", ")
        : value.src
    );
    sourceJpeg.setAttribute(
      "sizes",
      "(max-width: 600px) 400px, (max-width: 1200px) 800px, 1600px"
    );

    const fallbackImg = document.createElement("img");
    fallbackImg.setAttribute("src", value.src || "");
    fallbackImg.setAttribute("alt", value.alt || "");
    fallbackImg.setAttribute("title", value.attribution || "");
    fallbackImg.setAttribute("loading", "lazy");
    fallbackImg.setAttribute("decoding", "async");
    fallbackImg.setAttribute("width", value.width || "800");
    fallbackImg.setAttribute("height", value.height || "450");
    fallbackImg.style.maxWidth = "100%";
    fallbackImg.style.height = "auto";
    fallbackImg.style.display = "block";

    picture.appendChild(sourceWebP);
    picture.appendChild(sourceJpeg);
    picture.appendChild(fallbackImg);

    const figure = document.createElement("figure");
    figure.className = "ql-customImage-figure";
    figure.style.margin = "0";
    figure.style.padding = "0";

    figure.appendChild(picture);

    if (value.caption?.trim()) {
      const caption = document.createElement("figcaption");
      caption.className = "ql-customImage-caption";
      caption.textContent = value.caption;
      caption.style.fontSize = "0.875rem";
      caption.style.color = "#555";
      caption.style.marginTop = "4px";
      caption.style.fontStyle = "italic";
      figure.appendChild(caption);
    }

    node.appendChild(figure);

    return node;
  }

  static value(node) {
    const img = node.querySelector("img");
    const captionEl = node.querySelector(".ql-customImage-caption");

    return {
      src: img?.getAttribute("src") || "",
      alt: img?.getAttribute("alt") || "",
      attribution: img?.getAttribute("title") || "",
      caption: captionEl?.textContent || "",
    };
  }
}

// Register blot on both Quill instances
Quill.register(CustomImageBlot);

export default Quill;
